import React, { useState, useEffect, useRef } from 'react';
import { processError } from '../../services/ServiceUtils';
import ActionMessage from '../../components/ActionMessage/ActionMessage';
import { initializeModal } from '../../utils/ModalUtils';
import { getAddressWhitelist } from '../../services/AddressWhitelistService';
import { provisionSkimAutomation } from '../../services/WithdrawalsService';

// Presets de realização de lucro (espelham WITHDRAW_PROFILES do backend / ProfitRealizationPanel).
const PRESETS = [
    { key: 'SNOWBALL', emoji: '🐌', name: 'Bola de Neve', desc: 'Saca pouco (25%) — o bolo cresce rápido.' },
    { key: 'BALANCED', emoji: '⚖️', name: 'Equilibrado', desc: 'Metade sai (50%), metade fica compondo.' },
    { key: 'HARVEST', emoji: '🌾', name: 'Colheita', desc: 'Tira a maior parte (75%) do lucro.' },
];

/**
 * Wizard "1 clique" que monta a automação de realização de lucro pronta (template PROFIT_SKIM +
 * automação event-driven em REALIZED_PNL + ação WITHDRAW). props: { onSubmit }
 */
function WithdrawSkimWizard(props) {
    const [whitelist, setWhitelist] = useState([]);
    const [addressId, setAddressId] = useState('');
    const [profile, setProfile] = useState('');        // '' = manter config atual
    const [startNow, setStartNow] = useState(true);
    const [environment, setEnvironment] = useState('production');
    const [activate, setActivate] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const btnClose = useRef('');

    useEffect(() => {
        initializeModal('modalSkimWizard', () => { setMessage({ type: '', text: '' }); });
        getAddressWhitelist()
            .then(rows => setWhitelist((rows || []).filter(r => r.validated)))
            .catch(() => { });
    }, []);

    function onSubmit() {
        if (!addressId) { setMessage({ type: 'error', text: 'Escolha um endereço validado da whitelist.' }); return; }
        setSaving(true);
        provisionSkimAutomation({ addressId, profile: profile || undefined, startNow, environment, activate })
            .then(result => {
                setMessage({ type: 'success', text: `Automação "${result.automationName}" criada${result.isActive ? ' e ativada' : ''}!` });
                if (props.onSubmit) props.onSubmit(result);
            })
            .catch(err => setMessage({ type: 'error', text: processError(err) }))
            .finally(() => setSaving(false));
    }

    return (
        <div className="modal fade" id="modalSkimWizard" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title">Criar automação de realização de lucro</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div className="modal-body">
                        <p className='text-muted small'>
                            Monta tudo pra você: toda vez que houver lucro realizado novo, o bot saca uma fração
                            pro seu endereço e deixa o resto compondo. Os guardrails de segurança continuam valendo.
                        </p>

                        {whitelist.length === 0 ? (
                            <div className='alert alert-warning py-2'>
                                Você ainda não tem endereço <strong>validado</strong> na whitelist. Cadastre e valide um
                                em Modelos de Saque → Segurança antes de criar a automação.
                            </div>
                        ) : (
                            <div className='form-group mb-3'>
                                <label htmlFor='wzAddress'>Sacar para (endereço validado):</label>
                                <select id='wzAddress' className='form-select' value={addressId} onChange={e => setAddressId(e.target.value)}>
                                    <option value=''>— escolher —</option>
                                    {whitelist.map(w => (
                                        <option key={w.id} value={w.id}>{w.coin}/{w.network} · {w.label || `${w.address.slice(0, 14)}…`}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className='form-group mb-3'>
                            <label className='d-block mb-1'>Estilo (opcional — senão mantém sua configuração atual):</label>
                            <div className='row g-2'>
                                {PRESETS.map(p => (
                                    <div className='col-12' key={p.key}>
                                        <div
                                            className={`border rounded p-2 ${profile === p.key ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setProfile(profile === p.key ? '' : p.key)}>
                                            <strong>{p.emoji} {p.name}</strong>
                                            <span className='text-muted small d-block'>{p.desc}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className='form-check mb-2'>
                            <input className='form-check-input' type='checkbox' id='wzStartNow' checked={startNow} onChange={e => setStartNow(e.target.checked)} />
                            <label className='form-check-label' htmlFor='wzStartNow'>
                                Começar a contar de agora <span className='text-muted small'>(só realiza o lucro NOVO daqui pra frente)</span>
                            </label>
                        </div>
                        <div className='form-check mb-3'>
                            <input className='form-check-input' type='checkbox' id='wzActivate' checked={activate} onChange={e => setActivate(e.target.checked)} />
                            <label className='form-check-label' htmlFor='wzActivate'>Ativar a automação imediatamente</label>
                        </div>

                        <div className='form-group mb-2'>
                            <label className='d-block mb-1'>Ambiente:</label>
                            <div className='btn-group btn-group-sm' role='group'>
                                <button type='button' className={`btn ${environment === 'production' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setEnvironment('production')}>Produção</button>
                                <button type='button' className={`btn ${environment === 'test' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setEnvironment('test')}>Teste</button>
                            </div>
                        </div>

                        <ActionMessage
                            success={message.type === 'success' ? message.text : ''}
                            error={message.type === 'error' ? message.text : ''} />
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Fechar</button>
                        <button type="button" className="btn btn-primary" disabled={saving || whitelist.length === 0} onClick={onSubmit}>
                            {saving ? 'Criando…' : 'Criar automação'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WithdrawSkimWizard;
